import { useContext, useEffect } from "react";
import { AppContext } from "../../../Context/Context";
import useModalClose from "../../../hooks/useModalClose";
import Button from "../../Reusables/Button/Button";
import Close from "../../../assets/icons/close.png";
import "../../../assets/css/CustomizePopup/customizePopup.css";
import { useDispatch, useSelector } from "react-redux";
import {
  setDockDoorMode,
  setDockDoorViewIsOpen,
  setEnableBatteryStationObj,
  setEnableDockDoor,
  setEnableProductionLine,
  setLgvObjectViewIsOpen,
  setProductionLineViewIsOpen
} from "../../../store/plantSlice";

const MODAL_ID = "customize-wrapper-id";

const CustomizePopup = ({
  dashSwitches,
  setDashSwitches,
  onSettingsChange
}) => {
  const {
    setCustomizePopup,
    plantConfig,
    setRemovedLgvViewIsOpen,
    setDestinationPointersViewIsOpen,
    setEnableDestinationPointers
  } = useContext(AppContext);

  const enableBatteryStationObj = useSelector(
    (state) => state.plant.enableBatteryStationObj
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (onSettingsChange && dashSwitches) {
      onSettingsChange(dashSwitches);
    }
  }, [dashSwitches]);

  const disableAll = () => {
    const newSettings = {
      productionLines: false,
      removedLgv: false,
      dockDoorMode: false,
      dockDoors: false,
      lgvObjects: false,
      destinationLines: false,
      labelledMap: false,
      blockStorage: false,
      batteryStation: false
    };

    setDashSwitches(newSettings);

    dispatch(setLgvObjectViewIsOpen(false));
    dispatch(setProductionLineViewIsOpen(false));
    setRemovedLgvViewIsOpen(false);
    dispatch(setDockDoorViewIsOpen(false));
    dispatch(setDockDoorMode(false));
    dispatch(setEnableProductionLine(false));
    dispatch(setEnableDockDoor(false));
    setEnableDestinationPointers(false);
    dispatch(setEnableBatteryStationObj(false));
  };

  const enableAll = () => {
    const newSettings = {
      productionLines: true,
      removedLgv: true,
      dockDoorMode: true,
      dockDoors: true,
      lgvObjects: true,
      destinationLines: true,
      labelledMap: true,
      blockStorage: true,
      batteryStation: true
    };

    setDashSwitches(newSettings);

    dispatch(setLgvObjectViewIsOpen(true));
    dispatch(setProductionLineViewIsOpen(true));
    setRemovedLgvViewIsOpen(true);
    dispatch(setDockDoorViewIsOpen(true));
    dispatch(setDockDoorMode(true));
    dispatch(setEnableProductionLine(true));
    dispatch(setEnableDockDoor(true));
    setEnableDestinationPointers(true);
    dispatch(setEnableBatteryStationObj(true));
  };

  const updateSetting = (key, value) => {
    setDashSwitches((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const productionLineToggleSwitch = () => {
    const newValue = !dashSwitches.productionLines;
    updateSetting("productionLines", newValue);
    dispatch(setProductionLineViewIsOpen(newValue));
    dispatch(setEnableProductionLine(newValue));
  };

  const removedLgvToggleSwitch = () => {
    const newValue = !dashSwitches.removedLgv;
    updateSetting("removedLgv", newValue);
    setRemovedLgvViewIsOpen(newValue);
  };

  const dockDoorsToggleSwitch = () => {
    const newValue = !dashSwitches.dockDoors;
    updateSetting("dockDoors", newValue);
    dispatch(setDockDoorViewIsOpen(newValue));
    dispatch(setEnableDockDoor(newValue));
  };

  const lgvObjectsToggleSwitch = () => {
    const newValue = !dashSwitches.lgvObjects;
    updateSetting("lgvObjects", newValue);
    dispatch(setLgvObjectViewIsOpen(newValue));
  };

  const destinationLineToggleSwitch = () => {
    const newValue = !dashSwitches.destinationLines;
    updateSetting("destinationLines", newValue);
    setDestinationPointersViewIsOpen(newValue);
    setEnableDestinationPointers(newValue);
  };

  const dockDoorModeToggleSwitch = () => {
    const newValue = !dashSwitches.dockDoorMode;
    updateSetting("dockDoorMode", newValue);
    dispatch(setDockDoorMode(newValue));
  };

  const batteryStationToggleSwitch = () => {
    const newValue = !dashSwitches.batteryStation;
    updateSetting("batteryStation", newValue);
    dispatch(setEnableBatteryStationObj(newValue));
  };

  useModalClose(MODAL_ID, () => setCustomizePopup(false));

  return (
    <div id={MODAL_ID} className="customize-wrapper">
      <div className="customize-container">
        <div className="customize-modal-top-section">
          <img
            onClick={() => setCustomizePopup(false)}
            src={Close}
            alt="close_icon"
          />
        </div>
        <div className="customize-modal-middle-section">
          <div className="customize-buttons">
            <label className="switch">
              <input
                onClick={removedLgvToggleSwitch}
                type="checkbox"
                checked={dashSwitches.removedLgv}
              />
              <span className="slider round"></span>
            </label>
            <p>Removed LGVs</p>
          </div>
          {plantConfig.indoorMap?.productionLines?.isEnabled && (
            <div className="customize-buttons">
              <label className="switch">
                <input
                  onClick={productionLineToggleSwitch}
                  type="checkbox"
                  checked={dashSwitches.productionLines}
                />
                <span className="slider round"></span>
              </label>
              <p>Production Lines</p>
            </div>
          )}
          {plantConfig.indoorMap?.lgvs?.isEnabled && (
            <div className="customize-buttons">
              <label className="switch">
                <input
                  onClick={lgvObjectsToggleSwitch}
                  type="checkbox"
                  checked={dashSwitches.lgvObjects}
                />
                <span className="slider round"></span>
              </label>
              <p>LGV Objects</p>
            </div>
          )}
          {plantConfig.indoorMap?.destinationPointers?.isEnabled && (
            <div className="customize-buttons">
              <label className="switch">
                <input
                  onClick={destinationLineToggleSwitch}
                  type="checkbox"
                  checked={dashSwitches.destinationLines}
                />
                <span className="slider round"></span>
              </label>
              <p>Destination Pointer Lines</p>
            </div>
          )}
          {plantConfig.indoorMap?.batteryStations?.isEnabled && (
            <div className="customize-buttons">
              <label className="switch">
                <input
                  onClick={batteryStationToggleSwitch}
                  type="checkbox"
                  checked={enableBatteryStationObj}
                />
                <span className="slider round"></span>
              </label>
              <p>Battery Station Objects</p>
            </div>
          )}

          {plantConfig.indoorMap?.dockDoors?.isEnabled && (
            <div className="customize-buttons">
              <label className="switch">
                <input
                  onClick={dockDoorsToggleSwitch}
                  type="checkbox"
                  checked={dashSwitches.dockDoors}
                />
                <span className="slider round"></span>
              </label>
              <p>Dock Doors</p>
            </div>
          )}
          {plantConfig.indoorMap?.dockDoors?.isEnabled && (
            <div className="customize-buttons">
              <label className="switch">
                <input
                  onClick={dockDoorModeToggleSwitch}
                  type="checkbox"
                  checked={dashSwitches.dockDoorMode}
                />
                <span className="slider round"></span>
              </label>
              <p>
                {!dashSwitches.dockDoorMode
                  ? "Dock Door Simple Mode"
                  : "Dock Door Complex Mode"}
              </p>
            </div>
          )}
        </div>
        <div className="customize-modal-bottom-section">
          <Button
            onClick={disableAll}
            btnClass="cus-btn"
            placeholder="Disable All"
          />
          <Button
            onClick={enableAll}
            btnClass="cus-btn"
            placeholder="Enable All"
          />
        </div>
      </div>
    </div>
  );
};

export default CustomizePopup;
